/* eslint-disable react/display-name */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Background,
  Controls,
  ReactFlowProvider,
  ReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Typography from '../../../common/StyledTypography';
import BuilderStagesModal from './StageConfigModal';
import {
  WORKFLOW_DEFAULTS,
  NODE_CATEGORIES,
} from '../../../../constants/workflow.constant';
import axiosInstance from '../../../../axiosInstance';
import { useWorkflow, WORKFLOW_ACTIONS } from './WorkflowContext';
import { getNodeFormIds, findUpstreamNodeIds } from './utils/graphHelpers';
import useConnectionValidation from '../../../../hooks/features/workflows/useConnectionValidation';
import useNodeEventHandlers from '../../../../hooks/features/workflows/useNodeEventHandlers';
import useDragAndDrop from '../../../../hooks/features/workflows/useDragAndDrop';
import useWorkflowImperativeApi from '../../../../hooks/features/workflows/useWorkflowImperativeApi';

const defaultViewport = { x: 0, y: 0, zoom: 1 };

const WorkflowBuilder = forwardRef(
  (
    { paymentProviderId, paymentCurrency, paymentEnabled, paymentProviders },
    ref
  ) => {
    const { t } = useTranslation();
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [approvalTypes, setApprovalTypes] = useState([]);

    const {
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      openStageFormModal,
      dispatch,
    } = useWorkflow();

    useEffect(() => {
      async function fetchApprovalTypes() {
        try {
          const response = await axiosInstance.get(
            // eslint-disable-next-line no-undef
            `${process.env.REACT_APP_SERVER_URL}/api/approval_types`,
            { withCredentials: true }
          );
          setApprovalTypes(response?.data);
        } catch (error) {
          console.error('Error fetching approval types:', error);
        }
      }
      fetchApprovalTypes();
    }, []);

    const displayNodes = useMemo(() => {
      return nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          label: node.data?.label || (
            <Typography component="h6" variant="subtitle">
              {node.data?.name || t('workflowBuilder.stage')}
            </Typography>
          ),
        },
      }));
    }, [nodes, t]);

    const { resetWorkflow: resetWorkflowState, getWorkflowJsonData } =
      useWorkflowImperativeApi();

    const { onConnect } = useConnectionValidation();
    const {
      applyFormOnNode,
      handleCloseFormModal,
      onNodeClick,
      onNodeDoubleClick,
      onEdgeClick,
      clearDeletedNodeIds,
    } = useNodeEventHandlers({ approvalTypes });

    const resetWorkflow = useCallback(() => {
      resetWorkflowState();
      clearDeletedNodeIds();
    }, [resetWorkflowState, clearDeletedNodeIds]);

    useImperativeHandle(ref, () => ({ resetWorkflow, getWorkflowJsonData }), [
      resetWorkflow,
      getWorkflowJsonData,
    ]);
    const { onDrop, onDragOver } = useDragAndDrop({
      reactFlowInstance,
      approvalTypes,
    });

    const submissionNodes = useMemo(
      () =>
        nodes.filter((node) => node.category === NODE_CATEGORIES.SUBMISSION),
      [nodes]
    );

    const allSubmissionFormIds = useMemo(
      () => submissionNodes.flatMap(getNodeFormIds),
      [submissionNodes]
    );

    const getUsedSubmissionFormIds = useCallback(
      (currentNodeId) =>
        submissionNodes
          .filter((node) => node.id !== currentNodeId)
          .flatMap(getNodeFormIds),
      [submissionNodes]
    );

    const isNodeConnected = useCallback(
      (nodeId) => edges.some((edge) => edge.target === nodeId),
      [edges]
    );

    const getUpstreamSubmissionFormIds = useCallback(
      (nodeId) => {
        if (!nodeId || !isNodeConnected(nodeId)) return [];

        const upstreamIds = findUpstreamNodeIds(nodeId, edges, null);
        return [...upstreamIds].reduce((formIds, upstreamNodeId) => {
          const node = nodes.find((n) => n.id === upstreamNodeId);
          if (node?.category === NODE_CATEGORIES.SUBMISSION) {
            formIds.push(...getNodeFormIds(node));
          }
          return formIds;
        }, []);
      },
      [nodes, edges, isNodeConnected]
    );

    return (
      <div className="workflow_layout">
        <ReactFlowProvider>
          <div
            className="workflow_flow_container"
            onDrop={onDrop}
            onDragOver={onDragOver}
          >
            <ReactFlow
              nodes={displayNodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
              onNodeDoubleClick={onNodeDoubleClick}
              onInit={setReactFlowInstance}
              defaultViewport={defaultViewport}
            >
              <Controls />
              <Background
                color={WORKFLOW_DEFAULTS.BACKGROUND_COLOR}
                variant={WORKFLOW_DEFAULTS.BACKGROUND_VARIANT}
              />
            </ReactFlow>
          </div>
        </ReactFlowProvider>

        {openStageFormModal.open && (
          <BuilderStagesModal
            nodes={nodes}
            nodeStageId={openStageFormModal.nodeStageId}
            content={openStageFormModal.content}
            onEvent={(val) =>
              dispatch({
                type: WORKFLOW_ACTIONS.SET_OPEN_STAGE_FORM_MODAL,
                payload: val,
              })
            }
            onConfirm={applyFormOnNode}
            onDiscard={handleCloseFormModal}
            usedSubmissionFormIds={getUsedSubmissionFormIds(
              openStageFormModal.nodeStageId
            )}
            workflowSubmissionFormIds={allSubmissionFormIds}
            upstreamSubmissionFormIds={getUpstreamSubmissionFormIds(
              openStageFormModal.nodeStageId
            )}
            isNodeConnected={isNodeConnected(openStageFormModal.nodeStageId)}
            paymentProviderId={paymentProviderId}
            paymentCurrency={paymentCurrency}
            paymentEnabled={paymentEnabled}
            paymentProviders={paymentProviders}
          />
        )}
      </div>
    );
  }
);

export default WorkflowBuilder;
